import React, { useEffect, useMemo, useState } from "react";
import { CardIcon } from "../../icons/payments/Card";
import MASTERCardIcon from "../../icons/payments/MasterCard";
import VISACardIcon from "../../icons/payments/VisaCardIocn";
import "../../assets/css/CommenInput.css";
import JCB from "../../icons/payments/JCB.svg";
import amex from "../../icons/payments/amex.svg";
import dinersClub from "../../icons/payments/dinersClub.svg";
import DiscoverIcon from "../../icons/payments/Discover";
import UnionPay from "icons/payments/UnionPay";
import { BankIconSm } from "icons/payments/BankIconSm";
import CardFormComponent from "components/paymentMethod/CardFormComponent";
import { valuesProps } from "types/paymentMethods";
import expiryDateValidation, {
  decode,
  formatCards,
  getCardType,
  maskEmail,
  validateCard,
} from "utils/HelperFunctions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import Button from "components/form/Button";
import { useParams } from "react-router-dom";
import { useUserData } from "store/slices/authSlice";
import { useLeadData } from "store/slices/dashboardSlice";
import {
  addCard,
  updateCardList,
  useCardData,
} from "store/slices/paymentMethodSlice";
import { initialValues } from "utils/countryAddressList";
import { PaymentGateway } from "types/payments";
import { RootState } from "store/store";
import type {
  CardDetailsTabsProps,
  CountryWithStates,
  StateOption,
  FormattedPaymentMethodTypes,
} from "types/paymentMethodComponent";
import { PaymentMethodTypes } from "types/paymentMethods";
import { VisaOrCardIcon } from "components/paymentMethod/PaymentMethodWalletCard";
import { useTestAttrsFactory } from "utils/testid";
const CardDetailsTabs = ({
  img,
  name,
  desc,
  isSelected,
  cardType,
  icon,
  type = "card",
  cardInfo,
  handleSelect = () => {},
  showCardForm: showCardFormProp = false,
  setShowCardForm: setShowCardFormProp,
  defaultValue = "N",
  available = true,
  paypalid = "",
  accountEmail = "",
  paymentMethod = "",
  countriesWithStates = [],
  isLinked = true,
}: CardDetailsTabsProps): JSX.Element => {
  const testAttrs = useTestAttrsFactory();
  const [mouseHover, setMouseHover] = useState<boolean>(false);
  const [cardNumber, setcardNumber] = useState<string>("");
  const [iconType, setIconType] = useState<string>("");
  const [selectedState, setSelectedState] = useState<StateOption | undefined>(
    undefined
  );
  const [isErr, setIsErr] = useState<number>(-1);
  const [cardErrMsg, setCardErrMsg] = useState<string>("");
  const [isDateErr, setIsDateErr] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  // Saved-payment lists use this component directly.  In that case there is
  // no parent-owned editor state, so keep the expired-card editor interactive
  // locally while preserving support for a controlled caller.
  const [uncontrolledShowCardForm, setUncontrolledShowCardForm] =
    useState<boolean>(showCardFormProp);
  const showCardForm = setShowCardFormProp
    ? showCardFormProp
    : uncontrolledShowCardForm;
  const setShowCardForm =
    setShowCardFormProp || setUncontrolledShowCardForm;
  const { clusterId: cluster_id } = useParams<{ clusterId?: string }>();
  const clusterId = decode(cluster_id || "", true);
  const userData = useUserData();

  const [values, setValues] = useState<valuesProps>(initialValues);
  const [initialCardValues, setInitialCardValues] =
    useState<valuesProps>(initialValues);
  const { paymentPermission } = useAppSelector(
    (state: RootState) => state.payment
  );
  const dispatch = useAppDispatch();
  const accountLeadData = useLeadData();

  const cardList = useCardData();
  const imgUrl = (): React.ReactNode => {
    switch (cardType?.toLowerCase()) {
      case "visa":
        return <VISACardIcon />;
      case "amex":
      case "american express":
      case "americanexpress":
      case "american expres":
        return <img src={amex} alt="JCB" />;
      case "mc":
      case "master":
      case "mastercard":
        return <MASTERCardIcon />;
      case "discover":
      case "ds":
        return <DiscoverIcon />;
      case "jcb":
        return <img src={JCB} alt="JCB" />;
      case "diners":
      case "dinersclub":
      case "diners club":
      case "dc":
        return <img src={dinersClub} alt="DC" />;
      case "unionpay":
        return <UnionPay />;
      default:
        return type === "card" ? <CardIcon /> : <BankIconSm />;
    }
  };

  const handleClose = (): void => {
    setValues(initialValues);
    setInitialCardValues(initialValues);
    setIconType("");
    setcardNumber("");
    setSelectedState(undefined);
    setShowCardForm(false);
  };

  const handleClick = (): void => {
    if (!cardInfo) return;
    let data = cardInfo;
    setValues({
      ...values,
      x_card_num: data?.cc_4digit || "",
      x_exp_date: data?.cc_expiry,
      x_card_code: data?.cc_cvv || "",
      card_type: data?.cc_type || "",
      choldername: data?.account_name || "",
      x_country: data?.billing_country || "",
      x_address: data?.billing_address || "",
      address2: data?.billing_address2 || "",
      x_city: data?.billing_city || "",
      x_state: data?.billing_state || "",
      x_zip: data?.billing_zip || "",
    });
    setInitialCardValues({
      ...values,
      x_card_num: data?.cc_4digit || "",
      x_exp_date: data?.cc_expiry,
      x_card_code: data?.cc_cvv || "",
      card_type: data?.cc_type || "",
      choldername: data?.account_name || "",
      x_country: data?.billing_country || "",
      x_address: data?.billing_address || "",
      address2: data?.billing_address2 || "",
      x_city: data?.billing_city || "",
      x_state: data?.billing_state || "",
      x_zip: data?.billing_zip || "",
    });
    setIconType(data?.cc_type);
    setcardNumber(`XXXX XXXX XXXX ${data?.cc_4digit?.slice(-4)}`);

    const country = countriesWithStates?.find(
      (c: CountryWithStates) => c?.country_code === data?.billing_country
    );
    const selectedStateObject: StateOption | undefined = country?.states?.find(
      (s: StateOption) => s?.name === data?.billing_state
    );
    if (selectedStateObject) {
      setSelectedState({
        name: selectedStateObject.name,
        value: selectedStateObject.name,
      });
    } else {
      setSelectedState(undefined);
    }
    setShowCardForm(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | any,
    name: string
  ): void => {
    if (name === "x_card_num") {
      if (iconType === "amex") {
        if (e.target.value.length < 18) {
          const cardNumberValue = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumberValue
            .replace(/\D/g, "")
            .replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: e.target.value });
        }
      } else {
        if (e.target.value.length < 20) {
          const cardNumberValue = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumberValue
            .replace(/\D/g, "")
            .replace(/(\d{4})(?=\d)/g, "$1 ");
          setcardNumber(formattedCardNumber);
          setValues({ ...values, [name]: e.target.value });
        }
      }
    } else if (name === "x_card_code") {
      if (e.target.value.length < 4) {
        setValues({ ...values, [name]: e.target.value });
      }
    } else if (name === "x_exp_date") {
      const inputValue = e.target.value;
      setIsDateErr(false);
      if (inputValue?.length < 6) {
        const isValidDate: any = expiryDateValidation(inputValue);
        const formattedDate =
          inputValue?.length === 2 ? inputValue + "/" : inputValue;
        setValues({ ...values, [name]: formattedDate });
        !isValidDate?.isValid && inputValue?.length === 5 && setIsDateErr(true);
      }
    } else if (name === "x_country") {
      setValues({ ...values, [name]: e?.value });
    } else if (name === "x_state") {
      setValues({ ...values, [name]: e?.name });
    } else {
      setValues({ ...values, [name]: e?.target?.value });
    }
  };

  // button disable: compute once when dependencies change
  useEffect(() => {
    const isUnchanged =
      values?.x_card_num === initialCardValues?.x_card_num &&
      values?.x_exp_date === initialCardValues?.x_exp_date &&
      values?.x_card_code === initialCardValues?.x_card_code &&
      values?.x_city === initialCardValues?.x_city &&
      values?.x_address === initialCardValues?.x_address &&
      values?.address2 === initialCardValues?.address2 &&
      values?.x_zip === initialCardValues?.x_zip &&
      values?.x_country === initialCardValues?.x_country;

    let newDisabled = true;
    if (!isUnchanged) {
      newDisabled =
        values?.x_card_num === "" ||
        values?.x_exp_date === "" ||
        values?.x_card_code === "" ||
        values?.choldername === "" ||
        isErr !== -1 ||
        values?.x_city?.trim() === "" ||
        values?.x_address?.trim() === "" ||
        values?.x_zip?.trim() === "" ||
        values?.x_state?.trim() === "" ||
        values?.x_country?.trim() === "";
    }

    // Only update if value actually changes to avoid unnecessary renders
    setIsDisabled((prev) => (prev !== newDisabled ? newDisabled : prev));
  }, [values, initialCardValues, isErr]);

  useEffect(() => {
    if (cardNumber.includes("x")) return;
    if (!cardNumber) {
      setIconType("");
      setValues({ ...values, card_type: "" });
      return;
    } else {
      setIconType(getCardType(cardNumber));
      setValues({ ...values, card_type: getCardType(cardNumber) });
    }
  }, [cardNumber]);

  // Load countries and states from API when edit form is shown
  const onEditCard = async (): Promise<void> => {
    setIsLoading(true);
    try {
      let payload: valuesProps = {
        ...values,
        x_card_num: values?.x_card_num
          ? values?.x_card_num?.replaceAll(" ", "")
          : "",
        cluster_id: (clusterId ||
          userData?.data?.accountData?.clusterid) as number,
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        x_first_name: userData?.data?.accountData?.first_name,
        x_last_name: userData?.data?.accountData?.last_name,
        x_phone: userData?.data?.accountData?.phone,
        x_email: userData?.data?.accountData?.email,
        lead_id: accountLeadData?.lead_id,
        gateway_id: paymentPermission?.cc_gateway,
        gateway_name: paymentPermission?.cc_gateway_name?.name,
        account_holder_name: values?.choldername,
        card_type: iconType || values?.card_type,
        default: (cardInfo as PaymentMethodTypes)?.default,
        isUpdate: true,
        client_id: (cardInfo as PaymentMethodTypes)?.id,
        default_profile: (cardInfo as FormattedPaymentMethodTypes)
          ?.default_profile as "Y" | "N",
      };
      const res: any = await dispatch(addCard(payload)).unwrap();
      if (res && res?.body?.success) {
        handleSelect(
          "Card",
          (cardInfo as FormattedPaymentMethodTypes)?.id as number
        );
        let updatedlist = cardList.map((item: PaymentMethodTypes) => {
          if (item.id === (cardInfo as FormattedPaymentMethodTypes).id) {
            return {
              ...item,
              cc_4digit: values?.x_card_num?.slice(-4) || "",
              cc_expiry: values?.x_exp_date,
              cc_cvv: values?.x_card_code || "",
              cc_type: values?.card_type || "",
              account_name: values?.choldername || "",
              billing_country: values?.x_country || "",
              billing_address: values?.x_address || "",
              billing_address2: values?.address2 || "",
              billing_city: values?.x_city || "",
              billing_state: selectedState?.name || "",
              billing_zip: values?.x_zip || "",
            };
          } else {
            return { ...item };
          }
        });
        let updatedList = formatCards(updatedlist);
        await dispatch(updateCardList(updatedList));
        setShowCardForm(false);
      } else {
        setIsErr(4);
        setCardErrMsg(
          "An error occurred while saving your card. Try again later or with a different card."
        );
      }
    } catch (error) {
      console.error("error =====", error);
    } finally {
      setIsLoading(false);
    }
  };

  const description = useMemo(() => {
    if (type === "Wallets") {
      if (paymentMethod === "PP") {
        return <>{maskEmail(paypalid)}</>;
      } else if (paymentMethod === "AMZP" || paymentMethod === "CAPP") {
        return <>{maskEmail(accountEmail)}</>;
      } else if (paymentMethod === "AP" || paymentMethod === "GP") {
        return (
          <div className="flex items-center justify-start gap-[5px]">
            <VisaOrCardIcon type={cardInfo?.cc_type} />
            {cardInfo?.cc_4digit?.slice(-4)}
          </div>
        );
      }
    }
    return desc;
  }, [desc, type, cardInfo, paymentMethod, paypalid, accountEmail]);

  return (
    <div
      className={`${
        desc?.includes("Expired") || !available || !isLinked
          ? "bg-bgSecondary border-strokePrimary"
          : isSelected
            ? "border-btnOutlineStroke bg-btnBackGround"
            : "bg-bgThird border-strokePrimary hover:!bg-bgSecondary hover:!border-strokeActive"
      } rounded-[7px] border-[1px] px-[14px] py-[8px] `}
      {...testAttrs(`${type}-method-card`, `${name} payment method`)}
    >
      <div
        className={`flex items-center gap-x-[16px] `}
        onMouseOver={() => {
          setMouseHover(true);
        }}
        onMouseOut={() => {
          setMouseHover(false);
        }}
      >
        <div className="w-full h-full flex items-center gap-x-[16px] ">
          <input
            type="radio"
            id={"method.name"}
            aria-label={`Select ${name} payment method`}
            data-testid={testAttrs(`${type}-method-radio`)["data-testid"]}
            className={`dcdial_radio ${desc?.includes("Expired") || !available || !isLinked ? "cursor-default" : "cursor-default"}`}
            checked={isSelected}
            onChange={() => {}}
          />
          <div className="flex justify-center items-center w-[35px] h-[23px]">
            {icon ? (
              icon
            ) : imgUrl() ? (
              imgUrl()
            ) : (
              <div className="w-full h-full bg-white" />
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-[4px]">
              <div
                className={`text-xs leading-[15.62px] font-bold ${
                  desc?.includes("Expired") || !available || !isLinked
                    ? "text-txtSecondary"
                    : "text-txtPrimary"
                }`}
              >
                {name}
              </div>
              {desc?.includes("Expired") && (
                <div className="bg-[#FF0000] text-white font-medium leading-[13.02px] px-[6px] py-[1.5px] text-[10px] rounded-[4px]">
                  Expired
                </div>
              )}
              {!isLinked && available && (
                <div className="bg-[#FF0000] text-white font-medium leading-[13.02px] px-[6px] py-[1.5px] text-[10px] rounded-[4px]">
                  Unlinked
                </div>
              )}
              {defaultValue === "Y" && available && (
                <div className="bg-btnPrimaryInactive text-txtParagraph font-medium leading-[13.02px] px-[6px] py-[1.5px] text-[10px] rounded-[4px]">
                  Default
                </div>
              )}
              {!available && (
                <>
                  <div className="bg-yellow text-white font-medium leading-[13.02px] px-[6px] py-[1.5px] text-[10px] rounded-[4px]">
                    Unavailable
                  </div>
                </>
              )}
            </div>
            <div className="text-[10px] leading-[13.02px] font-normal  text-[#8790a5] pt-[4px]">
              {description}
            </div>
          </div>
        </div>
        {type === "card" &&
        desc?.includes("Expired") &&
        (mouseHover || showCardForm) &&
        paymentPermission?.cc_gateway_name?.name !== PaymentGateway.STRIPE ? (
          <div
            className="text-end text-blue hover:!text-btnPrimaryHover text-sm"
            role="button"
            tabIndex={0}
            {...testAttrs("edit-expired-card", "Edit expired card")}
            onClick={() => {
              if (showCardForm) {
                handleClose();
              } else {
                handleClick();
              }
            }}
          >
            {!showCardForm ? "Edit" : "Close"}
          </div>
        ) : null}
        {}
      </div>
      {type === "card" &&
      desc?.includes("Expired") &&
      showCardForm &&
      paymentPermission?.cc_gateway_name?.name !== PaymentGateway.STRIPE ? (
        <>
          <div className="w-full h-[1px] bg-strokePrimary my-[10px]" />
          <div className="flex flex-col w-full h-full gap-y-5">
            <CardFormComponent
              values={values}
              handleChange={handleChange}
              isEdit={true}
              cardNumber={cardNumber}
              iconType={iconType}
              isDateErr={isDateErr}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
              validateCard={() =>
                validateCard({
                  values,
                  iconType,
                  setIsErr,
                  setCardErrMsg,
                })
              }
              show={showCardForm}
              isErr={isErr}
              cardErrMsg={cardErrMsg}
            />
          </div>
          {isErr === 4 && (
            <div className="text-xs text-red pt-3">{cardErrMsg}</div>
          )}
          <Button
            text="Update"
            className="mt-3 w-full !py-[5.5px]"
            isDisabled={isDisabled}
            isLoading={isLoading}
            {...testAttrs("update-expired-card", "Update expired card")}
            onClick={onEditCard}
          />
        </>
      ) : null}
    </div>
  );
};

export default CardDetailsTabs;
